import { useState, useEffect } from 'react'
import { Link, createFileRoute } from '@tanstack/react-router'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Skeleton } from '@/components/ui/skeleton'
import { Checkbox } from '@/components/ui/checkbox'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table'
import { Progress } from '@/components/ui/progress'
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from '@/components/ui/command'
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover'
import { useTriggerPricingGeneration, useCancelPricingGeneration, useSavePricingConfig } from '@/hooks/useAdmin'
import { useQuery } from '@tanstack/react-query'
import { adminQueries } from '@/services/adminApi'
import { COUNTRIES_CONFIG, getUniqueGroups, getCountriesByGroup, getAvailableCountryCodes } from '@/config/countries'
import { getCountryName } from '@/lib/countries'
import { Flag } from '@/components/Flag'
import { ArrowLeft, Settings, Play, Loader2, CheckCircle2, XCircle, X, Plus, Check, Trash2 } from 'lucide-react'

type MarginEntry = { scope: string; type: string; multiply: string; add: string }
type MarginTierEntry = { maxCost: string; multiply: string; add: string }

function marginsToEntries(margins: Record<string, Record<string, any>>): MarginEntry[] {
  const entries: MarginEntry[] = []
  for (const [scope, types] of Object.entries(margins)) {
    for (const [type, value] of Object.entries(types)) {
      if (typeof value === 'object' && value !== null) {
        entries.push({ scope, type, multiply: String(value.multiply ?? 0), add: String(value.add ?? 0) })
      } else {
        entries.push({ scope, type, multiply: String(value ?? 0), add: '0' })
      }
    }
  }
  if (entries.length === 0) {
    entries.push({ scope: 'default', type: 'default', multiply: '0.60', add: '0' })
  }
  return entries
}

function entriesToMargins(entries: MarginEntry[]): Record<string, Record<string, any>> {
  const result: Record<string, Record<string, any>> = {}
  for (const e of entries) {
    const scope = e.scope.trim() || 'default'
    const type = e.type.trim() || 'default'
    const multiply = parseFloat(e.multiply) || 0
    const add = parseFloat(e.add) || 0
    if (!result[scope]) result[scope] = {}
    result[scope][type] = add !== 0 ? { multiply, add } : multiply
  }
  return result
}

function tiersToEntries(tiers: Array<Record<string, any>>): MarginTierEntry[] {
  if (!tiers || tiers.length === 0) return []
  return tiers.map((t) => ({
    maxCost: t.max_cost != null ? String(t.max_cost) : '',
    multiply: String(t.multiply ?? 0),
    add: String(t.add ?? 0),
  }))
}

function entriesToTiers(entries: MarginTierEntry[]): Array<Record<string, any>> {
  return entries.map((e) => {
    const tier: Record<string, any> = { multiply: parseFloat(e.multiply) || 0 }
    const add = parseFloat(e.add) || 0
    if (add !== 0) tier.add = add
    const maxCost = parseFloat(e.maxCost)
    if (!isNaN(maxCost) && e.maxCost.trim() !== '') tier.max_cost = maxCost
    return tier
  })
}

function GenerationControl() {
  const [autoActivate, setAutoActivate] = useState(true)
  const [isPolling, setIsPolling] = useState(false)
  const { data: runStatus, refetch } = useQuery({ ...adminQueries.pricingGenerationStatus(), refetchInterval: isPolling ? 2000 : false })
  const triggerGeneration = useTriggerPricingGeneration()
  const cancelGeneration = useCancelPricingGeneration()

  useEffect(() => {
    if (runStatus?.status === 'running' || runStatus?.status === 'pending') {
      setIsPolling(true)
    } else {
      setIsPolling(false)
    }
  }, [runStatus?.status])

  const handleGenerate = () => {
    triggerGeneration.mutate(autoActivate, {
      onSuccess: () => {
        setIsPolling(true)
        refetch()
      },
    })
  }

  const handleCancel = () => {
    if (runStatus?.id) {
      cancelGeneration.mutate(runStatus.id)
    }
  }

  const isRunning = runStatus?.status === 'running' || runStatus?.status === 'pending'
  const progress = runStatus && runStatus.countriesTotal > 0
    ? (runStatus.countriesProcessed / runStatus.countriesTotal) * 100
    : 0

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-lg">
          <Play className="h-5 w-5" />
          Generate Pricing
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="flex items-center gap-4">
          <Button
            onClick={handleGenerate}
            disabled={isRunning || triggerGeneration.isPending}
          >
            {isRunning ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Generating...
              </>
            ) : (
              'Generate Now'
            )}
          </Button>
          {isRunning && (
            <Button
              variant="destructive"
              size="sm"
              onClick={handleCancel}
              disabled={cancelGeneration.isPending}
            >
              {cancelGeneration.isPending ? (
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              ) : (
                <XCircle className="mr-2 h-4 w-4" />
              )}
              Cancel
            </Button>
          )}
          <div className="flex items-center gap-2">
            <Checkbox
              id="auto-activate"
              checked={autoActivate}
              onCheckedChange={(checked) => setAutoActivate(checked === true)}
            />
            <Label htmlFor="auto-activate" className="text-sm">Auto-activate</Label>
          </div>
        </div>

        {runStatus && (
          <div className="space-y-3">
            <div className="flex items-center gap-2">
              <Badge
                variant={
                  runStatus.status === 'completed' ? 'default'
                    : runStatus.status === 'failed' ? 'destructive'
                    : runStatus.status === 'running' ? 'secondary'
                    : 'outline'
                }
              >
                {runStatus.status === 'completed' && <CheckCircle2 className="mr-1 h-3 w-3" />}
                {runStatus.status === 'failed' && <XCircle className="mr-1 h-3 w-3" />}
                {runStatus.status === 'running' && <Loader2 className="mr-1 h-3 w-3 animate-spin" />}
                {runStatus.status}
              </Badge>
              {runStatus.countriesTotal > 0 && (
                <span className="text-sm text-muted-foreground">
                  {runStatus.countriesProcessed}/{runStatus.countriesTotal} countries
                </span>
              )}
              {runStatus.pricingVersionId && (
                <Link
                  to="/admin/pricing/versions"
                  className="text-sm text-primary underline"
                >
                  Version: {runStatus.pricingVersionId.slice(0, 8)}
                </Link>
              )}
            </div>

            {isRunning && runStatus.countriesTotal > 0 && (
              <Progress value={progress} className="h-2" />
            )}

            {runStatus.errorMessage && (
              <p className="text-sm text-destructive">{runStatus.errorMessage}</p>
            )}

            {runStatus.log && (
              <pre className="max-h-48 overflow-y-auto rounded-md bg-muted p-3 text-xs font-mono whitespace-pre-wrap">
                {runStatus.log}
              </pre>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  )
}

function ConfigEditor() {
  const { data: config, isLoading } = useQuery(adminQueries.pricingConfig())
  const saveConfig = useSavePricingConfig()

  const [countries, setCountries] = useState<string[]>([])
  const [targetCurrency, setTargetCurrency] = useState('GBP')
  const [marginEntries, setMarginEntries] = useState<MarginEntry[]>([
    { scope: 'default', type: 'default', multiply: '0.60', add: '0' },
  ])
  const [tierEntries, setTierEntries] = useState<MarginTierEntry[]>([])
  const [minPricePerMin, setMinPricePerMin] = useState('0.01')
  const [effectiveAgentPricePerMin, setAgentBillingRatePerMin] = useState('')
  const [initialized, setInitialized] = useState(false)
  const [open, setOpen] = useState(false)

  useEffect(() => {
    if (config && !initialized) {
      setCountries([...new Set(config.countries ?? [])])
      setTargetCurrency(config.targetCurrency ?? 'GBP')
      setMarginEntries(marginsToEntries(config.margins ?? {}))
      setTierEntries(tiersToEntries(config.marginTiers ?? []))
      setMinPricePerMin(String(config.minPricePerMin ?? 0.01))
      setAgentBillingRatePerMin(config.effectiveAgentPricePerMin != null ? String(config.effectiveAgentPricePerMin) : '')
      setInitialized(true)
    }
  }, [config, initialized])

  const updateMarginEntry = (index: number, field: keyof MarginEntry, value: string) => {
    setMarginEntries(prev => prev.map((e, i) => i === index ? { ...e, [field]: value } : e))
  }

  const addMarginEntry = () => {
    setMarginEntries(prev => [...prev, { scope: 'default', type: 'default', multiply: '0.60', add: '0' }])
  }

  const removeMarginEntry = (index: number) => {
    setMarginEntries(prev => prev.filter((_, i) => i !== index))
  }

  const updateTierEntry = (index: number, field: keyof MarginTierEntry, value: string) => {
    setTierEntries(prev => prev.map((e, i) => i === index ? { ...e, [field]: value } : e))
  }

  const addTierEntry = () => {
    setTierEntries(prev => [...prev, { maxCost: '', multiply: '0.40', add: '0' }])
  }

  const removeTierEntry = (index: number) => {
    setTierEntries(prev => prev.filter((_, i) => i !== index))
  }

  const handleSave = () => {
    const payload: Record<string, any> = {
      countries: countries,
      target_currency: targetCurrency,
      margins: entriesToMargins(marginEntries),
      margin_tiers: entriesToTiers(tierEntries),
      min_price_per_min: parseFloat(minPricePerMin) || 0.01,
      effective_agent_price_per_min: effectiveAgentPricePerMin.trim() !== '' ? parseFloat(effectiveAgentPricePerMin) : null,
    }
    if (config?.id) {
      payload.id = config.id
    }
    saveConfig.mutate(payload)
  }

  if (isLoading) return <Skeleton className="h-64 w-full" />

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <CardTitle className="flex items-center gap-2 text-lg">
            <Settings className="h-5 w-5" />
            Pricing Config
          </CardTitle>
          {config?.updatedAt && (
            <span className="text-xs text-muted-foreground">
              Last updated: {new Date(config.updatedAt).toLocaleString()}
            </span>
          )}
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="space-y-3">
          <Label>Countries</Label>
          <div className="flex flex-wrap gap-2 p-3 border border-border rounded-md min-h-[80px] bg-background">
            {countries.length === 0 && (
              <span className="text-sm text-muted-foreground">No countries selected</span>
            )}
            {countries.map((code) => {
              const country = COUNTRIES_CONFIG[code]
              return (
                <Badge key={code} variant="secondary" className="gap-1.5 pr-1">
                  {country && <Flag countryCode={country.code} size="sm" />}
                  <span>{code}</span>
                  <span className="text-muted-foreground">&middot;</span>
                  <span className="text-xs">{getCountryName(code)}</span>
                  <button
                    onClick={() => setCountries(countries.filter((c) => c !== code))}
                    className="ml-1 hover:bg-muted rounded-sm p-0.5"
                  >
                    <X className="h-3 w-3" />
                  </button>
                </Badge>
              )
            })}
          </div>
          <div className="flex flex-wrap gap-2">
            <Popover open={open} onOpenChange={setOpen}>
              <PopoverTrigger asChild>
                <Button variant="outline" size="sm" className="gap-2">
                  <Plus className="h-4 w-4" />
                  Add Countries
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-[400px] p-0" align="start">
                <Command>
                  <CommandInput placeholder="Search countries..." />
                  <CommandList>
                    <CommandEmpty>No countries found.</CommandEmpty>
                    <CommandGroup>
                      {Object.values(COUNTRIES_CONFIG)
                        .sort((a, b) => getCountryName(a.code).localeCompare(getCountryName(b.code)))
                        .map((country) => (
                          <CommandItem
                            key={country.code}
                            value={`${country.code} ${getCountryName(country.code)}`}
                            onSelect={() => {
                              if (!countries.includes(country.code)) {
                                setCountries([...countries, country.code])
                              }
                            }}
                          >
                            <Flag countryCode={country.code} size="sm" className="mr-2" />
                            <span className="flex-1">{getCountryName(country.code)}</span>
                            <span className="text-muted-foreground text-sm">{country.code}</span>
                            {countries.includes(country.code) && (
                              <Check className="ml-2 h-4 w-4 text-primary" />
                            )}
                          </CommandItem>
                        ))}
                    </CommandGroup>
                  </CommandList>
                </Command>
              </PopoverContent>
            </Popover>
            {getUniqueGroups().map((group) => (
              <Button
                key={group}
                variant="outline"
                size="sm"
                onClick={() => {
                  const groupCountries = getCountriesByGroup(group)
                  setCountries([...new Set([...countries, ...groupCountries])])
                }}
              >
                + {group}
              </Button>
            ))}
            <Button
              variant="outline"
              size="sm"
              onClick={() => {
                const allCodes = getAvailableCountryCodes()
                setCountries([...new Set([...countries, ...allCodes])])
              }}
            >
              + All
            </Button>
            {countries.length > 0 && (
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setCountries([])}
                className="text-destructive"
              >
                Clear All
              </Button>
            )}
          </div>
          <p className="text-xs text-muted-foreground">
            {countries.length} {countries.length === 1 ? 'country' : 'countries'} selected
          </p>
        </div>

        <div className="space-y-2 max-w-xs">
          <Label>Target Currency</Label>
          <Input value={targetCurrency} onChange={(e) => setTargetCurrency(e.target.value)} />
        </div>

        <div className="space-y-3">
          <Label>Margins</Label>
          <p className="text-xs text-muted-foreground">
            Scope: country ISO code or "default". Type: mobile, landline, etc. or "default" as fallback.
            Multiply is the percentage markup (e.g. 0.60 = 60%). Add is a flat per-minute cost added before markup.
          </p>
          <div className="rounded-md border">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-[140px]">Scope</TableHead>
                  <TableHead className="w-[140px]">Type</TableHead>
                  <TableHead className="w-[120px]">Multiply</TableHead>
                  <TableHead className="w-[120px]">Add/min</TableHead>
                  <TableHead className="w-[50px]" />
                </TableRow>
              </TableHeader>
              <TableBody>
                {marginEntries.map((entry, i) => (
                  <TableRow key={i}>
                    <TableCell className="p-1.5">
                      <Input
                        value={entry.scope}
                        onChange={(e) => updateMarginEntry(i, 'scope', e.target.value)}
                        placeholder="default"
                        className="h-8 text-sm font-mono"
                      />
                    </TableCell>
                    <TableCell className="p-1.5">
                      <Input
                        value={entry.type}
                        onChange={(e) => updateMarginEntry(i, 'type', e.target.value)}
                        placeholder="default"
                        className="h-8 text-sm font-mono"
                      />
                    </TableCell>
                    <TableCell className="p-1.5">
                      <Input
                        value={entry.multiply}
                        onChange={(e) => updateMarginEntry(i, 'multiply', e.target.value)}
                        type="number"
                        step="0.01"
                        className="h-8 text-sm font-mono"
                      />
                    </TableCell>
                    <TableCell className="p-1.5">
                      <Input
                        value={entry.add}
                        onChange={(e) => updateMarginEntry(i, 'add', e.target.value)}
                        type="number"
                        step="0.001"
                        className="h-8 text-sm font-mono"
                      />
                    </TableCell>
                    <TableCell className="p-1.5">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => removeMarginEntry(i)}
                        disabled={marginEntries.length <= 1}
                        className="h-8 w-8 p-0"
                      >
                        <Trash2 className="h-3.5 w-3.5 text-muted-foreground" />
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
          <Button variant="outline" size="sm" onClick={addMarginEntry} className="gap-1.5">
            <Plus className="h-3.5 w-3.5" />
            Add Rule
          </Button>
        </div>

        <div className="space-y-3">
          <Label>Margin Tiers</Label>
          <p className="text-xs text-muted-foreground">
            Cost-based tiers applied when no country-specific margin matches. Sorted by max cost ascending.
            Leave max cost empty on the last row as a catch-all. Country overrides always take precedence over tiers.
          </p>
          {tierEntries.length > 0 && (
            <div className="rounded-md border">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="w-[160px]">Max Cost/min</TableHead>
                    <TableHead className="w-[120px]">Multiply</TableHead>
                    <TableHead className="w-[120px]">Add/min</TableHead>
                    <TableHead className="w-[50px]" />
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {tierEntries.map((entry, i) => (
                    <TableRow key={i}>
                      <TableCell className="p-1.5">
                        <Input
                          value={entry.maxCost}
                          onChange={(e) => updateTierEntry(i, 'maxCost', e.target.value)}
                          type="number"
                          step="0.01"
                          placeholder="no limit (catch-all)"
                          className="h-8 text-sm font-mono"
                        />
                      </TableCell>
                      <TableCell className="p-1.5">
                        <Input
                          value={entry.multiply}
                          onChange={(e) => updateTierEntry(i, 'multiply', e.target.value)}
                          type="number"
                          step="0.01"
                          className="h-8 text-sm font-mono"
                        />
                      </TableCell>
                      <TableCell className="p-1.5">
                        <Input
                          value={entry.add}
                          onChange={(e) => updateTierEntry(i, 'add', e.target.value)}
                          type="number"
                          step="0.001"
                          className="h-8 text-sm font-mono"
                        />
                      </TableCell>
                      <TableCell className="p-1.5">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => removeTierEntry(i)}
                          className="h-8 w-8 p-0"
                        >
                          <Trash2 className="h-3.5 w-3.5 text-muted-foreground" />
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
          <Button variant="outline" size="sm" onClick={addTierEntry} className="gap-1.5">
            <Plus className="h-3.5 w-3.5" />
            Add Tier
          </Button>
        </div>

        <div className="space-y-2 max-w-xs">
          <Label>Min Price/min</Label>
          <p className="text-xs text-muted-foreground">
            Floor price per minute. Even zero-cost provider rates will charge at least this amount. Default: 0.01
          </p>
          <Input
            value={minPricePerMin}
            onChange={(e) => setMinPricePerMin(e.target.value)}
            type="number"
            step="0.001"
            min="0"
          />
        </div>

        <div className="space-y-2 max-w-xs">
          <Label>Effective agent price/min</Label>
          <p className="text-xs text-muted-foreground">
            Flat per-minute rate charged to customers for AI agent usage, independent of provider costs.
            Leave blank to fall back to provider cost × margin (env: <code>AGENT_MARGIN_MULTIPLIER</code>).
          </p>
          <Input
            value={effectiveAgentPricePerMin}
            onChange={(e) => setAgentBillingRatePerMin(e.target.value)}
            type="number"
            step="0.001"
            min="0"
            placeholder="e.g. 0.05"
          />
        </div>

        <div className="flex justify-end">
          <Button onClick={handleSave} disabled={saveConfig.isPending}>
            {saveConfig.isPending ? 'Saving...' : config?.id ? 'Update Config' : 'Create Config'}
          </Button>
        </div>
      </CardContent>
    </Card>
  )
}

export const Route = createFileRoute('/admin/pricing/generate')({
  component: AdminPricingGeneratePage,
})

function AdminPricingGeneratePage() {
  return (
    <div className="space-y-6">
      <div className="flex items-center gap-3">
        <Button variant="ghost" size="sm" asChild>
          <Link to="/admin/pricing">
            <ArrowLeft className="mr-1 h-4 w-4" />
            Pricing
          </Link>
        </Button>
        <h1 className="text-3xl font-bold">Generate Pricing</h1>
      </div>

      <GenerationControl />

      <ConfigEditor />
    </div>
  )
}
